import { SITE_PAGES } from './pages';
import { SITE_REDIRECTS } from './redirects';
import type { SitePage } from './types';

export type { SitePage, SiteRedirect, PageKind, LlmsSection, PageSeoFields } from './types';
export { SITE_PAGES } from './pages';
export { SITE_REDIRECTS } from './redirects';

export function normalizeContentPath(path: string): string {
  if (!path || path === '/') return '/';
  const withSlash = path.startsWith('/') ? path : `/${path}`;
  return withSlash.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';
}

export function getSitePage(path: string): SitePage | undefined {
  const normalized = normalizeContentPath(path);
  return SITE_PAGES.find((page) => normalizeContentPath(page.path) === normalized);
}

/** Throws if path missing — use when wiring required pages. */
export function requireSitePage(path: string): SitePage {
  const page = getSitePage(path);
  if (!page) {
    throw new Error(
      `Missing SITE_PAGES entry for "${path}". Add it in src/content/pages.ts`,
    );
  }
  return page;
}

/** Shape expected by SeoHead / ServiceLandingPage seo prop. */
export function getPageSeo(path: string) {
  const page = requireSitePage(path);
  return {
    path: page.path,
    title: page.seo.title,
    description: page.seo.description,
    keywords: page.seo.keywords,
    image: page.seo.image,
    imageAlt: page.seo.imageAlt,
    noindex: page.seo.noindex,
  };
}

export function getSitemapPages(): SitePage[] {
  return SITE_PAGES.filter((page) => page.sitemap !== false);
}

export function getPrimaryNavLinks() {
  return SITE_PAGES.filter((page) => page.navPrimary)
    .slice()
    .sort((a, b) => (a.navOrder ?? 999) - (b.navOrder ?? 999))
    .map((page) => ({
      to: page.path,
      label: page.path === '/' ? 'Home' : page.label === 'About' ? 'About Us' : page.label === 'Contact' ? 'Get In Touch' : page.label,
    }));
}

export function getFooterServiceLinks() {
  return SITE_PAGES.filter((page) => page.footerService)
    .slice()
    .sort((a, b) => (a.navOrder ?? 999) - (b.navOrder ?? 999))
    .map((page) => ({
      to: page.path,
      label: page.path === '/services' ? 'All services' : page.label,
    }));
}

export function getFooterCompanyLinks() {
  return SITE_PAGES.filter((page) => page.footerCompany)
    .slice()
    .sort((a, b) => (a.navOrder ?? 999) - (b.navOrder ?? 999))
    .map((page) => ({ to: page.path, label: page.path === '/blog' ? 'Insights' : page.label }));
}

export function getLlmsPages(section: 'core' | 'specialized') {
  return SITE_PAGES.filter((page) => page.llms === section).sort(
    (a, b) => (a.navOrder ?? 999) - (b.navOrder ?? 999),
  );
}

export { SITE_REDIRECTS as redirects };
